import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage, t } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('portalToken')}` } });

const AVAIL_OPTIONS = (lang) => [
  { value: 'available',   label: t('Available',   'Disponible',       lang), color: 'success' },
  { value: 'if_needed',   label: t('If Needed',   'Si es Necesario',  lang), color: 'warning' },
  { value: 'unavailable', label: t('Unavailable', 'No Disponible',    lang), color: 'danger'  },
];

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(t) {
  if (!t) return null;
  return t.slice(0, 5);
}

function todayISO() { return new Date().toISOString().slice(0, 10); }

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = x => x * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function PortalEventSchedulePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingDay, setSavingDay] = useState(null);
  const [confirmingDay, setConfirmingDay] = useState(null);
  const [pendingAvail, setPendingAvail] = useState(null); // { dayId, status, selectedShifts: Set, notes }

  // Clock-in/out state
  const [punchStatus, setPunchStatus] = useState(null);   // null | { canPunch, action, openEntry, event }
  const [geoPos, setGeoPos]           = useState(null);   // { lat, lng } | null
  const [geoError, setGeoError]       = useState('');
  const [geoLoading, setGeoLoading]   = useState(false);
  const [punching, setPunching]       = useState(false);
  const [punchMsg, setPunchMsg]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/portal/events/${eventId}/schedule`, auth());
      setData(r.data);
    } catch {
      setError(t('Failed to load schedule.', 'Error al cargar el horario.', lang));
    } finally {
      setLoading(false);
    }
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPunchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/portal/events/${eventId}/punch-status`, auth());
      setPunchStatus(r.data);
    } catch { /* not an event day — hide punch card */ }
  }, [eventId]);

  useEffect(() => { load(); loadPunchStatus(); }, [load, loadPunchStatus]);

  // Watch live geolocation while page is open
  useEffect(() => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    const watcher = navigator.geolocation.watchPosition(
      pos => {
        setGeoPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoError('');
        setGeoLoading(false);
      },
      () => {
        setGeoError(t('Location unavailable. Check browser permissions.', 'Ubicación no disponible. Revise permisos del navegador.', lang));
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watcher);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePunch = async () => {
    setPunching(true); setPunchMsg('');
    try {
      const body = geoPos ? { lat: geoPos.lat, lng: geoPos.lng } : {};
      const r = await axios.post(`${API_URL}/api/portal/events/${eventId}/punch`, body, auth());
      const action = r.data.action;
      setPunchMsg(action === 'clock_in'
        ? t('Clocked in successfully!', '¡Entrada registrada con éxito!', lang)
        : t('Clocked out successfully!', '¡Salida registrada con éxito!', lang));
      await loadPunchStatus();
    } catch (err) {
      setPunchMsg(err?.response?.data?.error || t('Punch failed.', 'Error al registrar.', lang));
    } finally {
      setPunching(false);
    }
  };

  const handleAvailability = (dayId, status, dayData) => {
    if (status === 'unavailable') {
      setSavingDay(dayId);
      axios.post(`${API_URL}/api/portal/events/${eventId}/availability`, { eventDayId: dayId, status }, auth())
        .then(() => {
          setData(prev => ({
            ...prev,
            days: prev.days.map(d =>
              d.id === dayId ? { ...d, myAvailability: { ...(d.myAvailability || {}), status, availShifts: null } } : d
            ),
          }));
          if (pendingAvail?.dayId === dayId) setPendingAvail(null);
        })
        .catch(err => setError(err?.response?.data?.message || t('Failed to save availability.', 'Error al guardar la disponibilidad.', lang)))
        .finally(() => setSavingDay(null));
    } else {
      // Pre-select all shifts from existing availability, or none
      const existing = dayData?.myAvailability?.availShifts;
      const preSelected = existing ? new Set(existing.split(',').map(Number).filter(Boolean)) : new Set();
      setPendingAvail({
        dayId,
        status,
        selectedShifts: preSelected,
        notes: dayData?.myAvailability?.notes || '',
      });
    }
  };

  const handleSaveAvail = async () => {
    if (!pendingAvail) return;
    const { dayId, status, selectedShifts, notes } = pendingAvail;
    const availShifts = selectedShifts.size > 0 ? [...selectedShifts].sort().join(',') : null;
    setSavingDay(dayId);
    try {
      await axios.post(`${API_URL}/api/portal/events/${eventId}/availability`, {
        eventDayId: dayId, status, availShifts, notes: notes || null,
      }, auth());
      setData(prev => ({
        ...prev,
        days: prev.days.map(d =>
          d.id === dayId
            ? { ...d, myAvailability: { ...(d.myAvailability || {}), status, availShifts, notes: notes || null } }
            : d
        ),
      }));
      setPendingAvail(null);
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to save availability.', 'Error al guardar la disponibilidad.', lang));
    } finally {
      setSavingDay(null);
    }
  };

  const handleConfirm = async (schedId) => {
    setConfirmingDay(schedId);
    try {
      await axios.patch(`${API_URL}/api/portal/events/${eventId}/schedule/${schedId}/confirm`, {}, auth());
      setData(prev => ({
        ...prev,
        days: prev.days.map(d =>
          d.myAssignment?.id === schedId
            ? { ...d, myAssignment: { ...d.myAssignment, confirmedByEmployee: true } }
            : d
        ),
      }));
    } catch { /* ignore */ }
    finally { setConfirmingDay(null); }
  };

  if (loading) return <div className="text-center py-5"><span className="spinner-border spinner-border-sm" /></div>;
  if (error) return <div className="alert alert-warning m-3" style={{ fontSize: 13 }}>{error}</div>;
  if (!data) return null;

  const { event, days, isPoolMember } = data;
  const today = todayISO();
  const options = AVAIL_OPTIONS(lang);

  const upcoming = days.filter(d => d.date >= today && (!d.myAssignment || !d.myAssignment.published));
  const published = days.filter(d => d.myAssignment?.published);
  const past = days.filter(d => d.date < today && (!d.myAssignment || !d.myAssignment.published));

  return (
    <div style={{ padding: '16px' }}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-link p-0 text-muted" style={{ fontSize: 12 }} onClick={() => navigate('/portal/events')}>
          ← {t('Events', 'Eventos', lang)}
        </button>
      </div>
      <h6 className="fw-bold mb-0">{event.name}</h6>
      <div className="text-muted mb-4" style={{ fontSize: 12 }}>
        {fmtDate(event.startDate)} – {fmtDate(event.endDate)}
        {!isPoolMember && (
          <span className="ms-2 badge bg-secondary" style={{ fontSize: 10 }}>
            {t('Not in pool', 'Sin rol asignado', lang)}
          </span>
        )}
      </div>

      {/* ── Event Clock-In / Out ── */}
      {punchStatus?.canPunch && (() => {
        const ev = punchStatus.event;
        const isClockedIn = punchStatus.action === 'clock_out';
        const dist = (ev.venueLat != null && ev.venueLng != null && geoPos)
          ? Math.round(haversineMeters(geoPos.lat, geoPos.lng, ev.venueLat, ev.venueLng))
          : null;
        const withinRadius = dist != null ? dist <= ev.geoRadius : ev.venueLat == null;
        const canClick = !punching && withinRadius && (!ev.venueLat || geoPos);

        return (
          <div className={`card mb-4 border-${isClockedIn ? 'danger' : 'success'}`}
            style={{ borderWidth: 2 }}>
            <div className={`card-header py-2 bg-${isClockedIn ? 'danger' : 'success'} text-white`}
              style={{ fontSize: 13, fontWeight: 600 }}>
              {isClockedIn
                ? t("You're clocked in", 'Estás registrado', lang)
                : t("Clock in for today's event", 'Registrar entrada al evento de hoy', lang)}
            </div>
            <div className="card-body py-3 px-3">
              {ev.venueAddress && (
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  📍 {ev.venueAddress}
                </div>
              )}
              {isClockedIn && punchStatus.openEntry && (
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  {t('Clocked in at', 'Entrada a las', lang)}{' '}
                  <strong>{new Date(punchStatus.openEntry.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                </div>
              )}

              {/* Distance indicator */}
              {ev.venueLat != null && (
                <div className="mb-3">
                  {geoLoading && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      <span className="spinner-border spinner-border-sm me-1" />
                      {t('Getting your location…', 'Obteniendo ubicación…', lang)}
                    </div>
                  )}
                  {geoError && !geoLoading && (
                    <div className="text-danger" style={{ fontSize: 12 }}>⚠ {geoError}</div>
                  )}
                  {dist != null && !geoLoading && (
                    <div className={`fw-semibold`} style={{ fontSize: 13, color: withinRadius ? '#198754' : '#dc3545' }}>
                      {withinRadius
                        ? `✓ ${t('You are', 'Estás a', lang)} ${dist} m ${t('from the venue', 'del lugar', lang)}`
                        : `✗ ${t('You are', 'Estás a', lang)} ${dist} m ${t('from the venue', 'del lugar', lang)} — ${t('must be within', 'debes estar dentro de', lang)} ${ev.geoRadius} m`}
                    </div>
                  )}
                  {!geoPos && !geoLoading && !geoError && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t('Waiting for location…', 'Esperando ubicación…', lang)}
                    </div>
                  )}
                </div>
              )}

              {punchMsg && (
                <div className={`alert py-2 mb-3 ${punchMsg.includes('success') || punchMsg.includes('éxito') ? 'alert-success' : 'alert-danger'}`}
                  style={{ fontSize: 13 }}>
                  {punchMsg}
                </div>
              )}

              <button
                className={`btn btn-lg w-100 btn-${isClockedIn ? 'danger' : 'success'}`}
                onClick={handlePunch}
                disabled={!canClick}
                style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}
              >
                {punching
                  ? <span className="spinner-border spinner-border-sm me-2" />
                  : null}
                {isClockedIn
                  ? t('Clock Out', 'Registrar Salida', lang)
                  : t('Clock In', 'Registrar Entrada', lang)}
              </button>

              {ev.venueLat != null && !withinRadius && !geoLoading && (
                <div className="text-muted text-center mt-2" style={{ fontSize: 11 }}>
                  {t('Move closer to the venue to enable clock-in.', 'Acérquese al lugar para habilitar el registro.', lang)}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Published schedule — days I'm assigned to */}
      {published.length > 0 && (
        <div className="mb-4">
          <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
            {t('My Schedule', 'Mi Horario', lang)}
          </div>
          <div className="d-flex flex-column gap-2">
            {published.map(d => {
              const a = d.myAssignment;
              return (
                <div key={d.id} className="card border-success">
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>{fmtDate(d.date)}</div>
                        {a.shiftStart && (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {fmtTime(a.shiftStart)}{a.shiftEnd ? ` – ${fmtTime(a.shiftEnd)}` : ''}
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-success" style={{ fontSize: 10 }}>
                          {t('Scheduled', 'Programado', lang)}
                        </span>
                        {!a.confirmedByEmployee && (
                          <button
                            className="btn btn-sm btn-outline-success"
                            style={{ fontSize: 11 }}
                            onClick={() => handleConfirm(a.id)}
                            disabled={confirmingDay === a.id}
                          >
                            {confirmingDay === a.id ? '…' : t('Confirm', 'Confirmar', lang)}
                          </button>
                        )}
                        {a.confirmedByEmployee && (
                          <span className="badge bg-light text-success border" style={{ fontSize: 10 }}>
                            ✓ {t('Confirmed', 'Confirmado', lang)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Availability form — upcoming unpublished days */}
      {upcoming.length > 0 && (
        <div className="mb-4">
          <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>
            {t('Availability', 'Disponibilidad', lang)}
          </div>
          <p className="text-muted mb-2" style={{ fontSize: 12 }}>
            {t(
              'Let your manager know your availability for upcoming days.',
              'Informe a su gerente sobre su disponibilidad para los próximos días.',
              lang
            )}
          </p>
          <div className="d-flex flex-column gap-2">
            {upcoming.map(d => {
              const avail = d.myAvailability;
              const isSaving = savingDay === d.id;
              const isPending = pendingAvail?.dayId === d.id;

              // Event hours / shift context
              const hasHours = d.eventStart || d.eventEnd;
              const hasShifts = d.shiftCount && d.shift1Start;

              return (
                <div key={d.id} className="card">
                  <div className="card-body py-2 px-3">
                    <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>{fmtDate(d.date)}</div>

                    {/* Event hours context */}
                    {hasHours && (
                      <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                        🕐 {t('Event:', 'Evento:', lang)} {fmtTime(d.eventStart) || '?'} – {fmtTime(d.eventEnd) || '?'}
                      </div>
                    )}
                    {hasShifts && (
                      <div className="text-muted mb-2" style={{ fontSize: 11 }}>
                        {Array.from({ length: d.shiftCount || 1 }, (_, i) => i + 1).map((n, idx) => {
                          const start = fmtTime(d[`shift${n}Start`]);
                          const end   = fmtTime(d[`shift${n}End`]);
                          return (
                            <span key={n}>
                              {idx > 0 && ' · '}
                              {t(`Shift ${n}:`, `Turno ${n}:`, lang)} {start || '?'}{end ? `–${end}` : ''}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {!hasHours && !hasShifts && <div className="mb-2" />}

                    {/* Status buttons */}
                    <div className="d-flex gap-2 flex-wrap">
                      {options.map(opt => {
                        const isActive = (!isPending && avail?.status === opt.value) || (isPending && pendingAvail.status === opt.value);
                        return (
                        <button
                          key={opt.value}
                          className={`btn btn-sm ${isActive ? `btn-${opt.color}` : `btn-outline-${opt.color}`}`}
                          style={{ fontSize: 12 }}
                          onClick={() => handleAvailability(d.id, opt.value, d)}
                          disabled={isSaving}
                        >
                          {isSaving && opt.value === 'unavailable' ? '…' : opt.label}
                        </button>
                        );
                      })}
                    </div>

                    {/* Inline shift-selection form when Available / If Needed tapped */}
                    {isPending && (() => {
                      const shiftCount = d.shiftCount || 1;
                      const shiftDefs = Array.from({ length: shiftCount }, (_, i) => {
                        const n = i + 1;
                        const start = fmtTime(d[`shift${n}Start`]);
                        const end   = fmtTime(d[`shift${n}End`]);
                        return { n, start, end };
                      });
                      return (
                        <div className="mt-3 p-2 rounded" style={{ background: '#f0f4ff', border: '1px solid #c9d4f5' }}>
                          <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>
                            {t('Which shifts are you available for?', '¿Para qué turnos estás disponible?', lang)}
                          </div>
                          {shiftDefs.length > 0 ? (
                            <div className="d-flex flex-column gap-2 mb-2">
                              {shiftDefs.map(({ n, start, end }) => {
                                const selected = pendingAvail.selectedShifts.has(n);
                                return (
                                  <button
                                    key={n}
                                    type="button"
                                    className={`btn btn-sm text-start ${selected ? 'btn-success' : 'btn-outline-success'}`}
                                    style={{ fontSize: 13 }}
                                    onClick={() => setPendingAvail(p => {
                                      const next = new Set(p.selectedShifts);
                                      if (next.has(n)) next.delete(n); else next.add(n);
                                      return { ...p, selectedShifts: next };
                                    })}
                                  >
                                    <span className="fw-semibold">{t('Shift', 'Turno', lang)} {n}</span>
                                    {start && (
                                      <span className="ms-2 text-muted" style={{ fontSize: 11, fontWeight: 400 }}>
                                        {start}{end ? ` – ${end}` : ''}
                                      </span>
                                    )}
                                    {selected && <span className="ms-2">✓</span>}
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-muted" style={{ fontSize: 12 }}>
                              {t('No shifts configured for this day.', 'No hay turnos configurados para este día.', lang)}
                            </p>
                          )}
                          <div className="mb-2">
                            <label style={{ fontSize: 11, color: '#666' }}>{t('Notes (optional)', 'Notas (opcional)', lang)}</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              style={{ fontSize: 12 }}
                              placeholder={t('e.g. prefer morning', 'ej. prefiero mañana', lang)}
                              value={pendingAvail.notes}
                              onChange={e => setPendingAvail(p => ({ ...p, notes: e.target.value }))}
                            />
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-primary"
                              style={{ fontSize: 12 }}
                              onClick={handleSaveAvail}
                              disabled={isSaving}
                            >
                              {isSaving ? '…' : t('Save', 'Guardar', lang)}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              style={{ fontSize: 12 }}
                              onClick={() => setPendingAvail(null)}
                              disabled={isSaving}
                            >
                              {t('Cancel', 'Cancelar', lang)}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Current saved availability summary */}
                    {avail && !isPending && (
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        {t('Marked:', 'Marcado:', lang)} <strong>{options.find(o => o.value === avail.status)?.label}</strong>
                        {avail.availShifts && (
                          <span className="ms-2">
                            · {t('Shifts', 'Turnos', lang)}: <strong>{avail.availShifts}</strong>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Past days */}
      {past.length > 0 && (
        <div>
          <div className="fw-semibold mb-2 text-muted" style={{ fontSize: 12 }}>
            {t('Past Days', 'Días Pasados', lang)}
          </div>
          <div className="d-flex flex-column gap-1">
            {past.map(d => (
              <div key={d.id} className="d-flex justify-content-between align-items-center py-1 px-2 rounded" style={{ background: '#f8f9fa', fontSize: 12 }}>
                <span className="text-muted">{fmtDate(d.date)}</span>
                {d.myAvailability ? (
                  <span className={`badge bg-${options.find(o => o.value === d.myAvailability.status)?.color || 'secondary'}`} style={{ fontSize: 10 }}>
                    {options.find(o => o.value === d.myAvailability.status)?.label}
                  </span>
                ) : (
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    {t('No response', 'Sin respuesta', lang)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {days.length === 0 && (
        <p className="text-muted text-center py-4" style={{ fontSize: 13 }}>
          {t('No days scheduled for this event yet.', 'No hay días programados para este evento aún.', lang)}
        </p>
      )}
    </div>
  );
}
